import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from '../../context/AuthContext.js';
import './HourReports.css';
import ReportHours from './HourValue';

const HourReports = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = sessionStorage.getItem('jwtToken');
  useAuth();

  const generateMonths = () => {
    const months = [];
    const today = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'long'
      });

      months.push({
        value: monthKey,
        label: monthName,
        year,
        month
      });
    }

    return months;
  };

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    setSelectedMonth(`${currentYear}-${currentMonth.toString().padStart(2, '0')}`);
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/users`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setUsers(response.data.data || []);
      } catch (error) {
        console.error('Fout bij ophalen gebruikers:', error);
      }
    };

    fetchUsers();
  }, [token]);

  const fetchReport = useCallback(async () => {
    if (!selectedMonth) {
      setReportData(null);
      return;
    }

    setLoading(true);

    try {
      const [year, month] = selectedMonth.split('-');

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/monthly-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            year: parseInt(year, 10),
            month: parseInt(month, 10),
            user_id: selectedUser !== 'all' ? selectedUser : undefined
          }
        }
      );

      setReportData(response.data.data || null);
    } catch (error) {
      console.error('Fout bij ophalen rapportage:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUser, token]);

  useEffect(() => {
    if (selectedMonth) {
      fetchReport();
    }
  }, [selectedMonth, fetchReport]);

  const months = generateMonths();

  const DailyHoursCell = ({ hours }) => {
    if (hours == null || Number(hours) === 0) {
      return <td className="text-center text-muted">-</td>;
    }
    return <td className="text-center"><ReportHours value={hours} /></td>;
  };

  const EmployeeCard = ({ employee, monthDays }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div className="card mb-3">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">{employee.employee_name}</h6>
            </div>
            <div className="text-end">
              <div className="fw-bold"><ReportHours value={employee.monthly_total} /> uur</div>
              <small className="text-muted">Maand totaal</small>
            </div>
          </div>
        </div>

        <div className="card-body">
          <button
            className="btn btn-outline-secondary btn-sm w-100 mb-3"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "↑ Verberg details" : "↓ Toon dagelijkse uren"}
          </button>

          {expanded && (
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Taak</th>
                    {monthDays.map((day) => (
                      <th key={day} className="text-center" style={{ fontSize: '0.75rem' }}>
                        {day}
                      </th>
                    ))}
                    <th className="text-center">Totaal</th>
                  </tr>
                </thead>
                <tbody>
                  {(employee.tasks || []).map((task, index) => (
                    <tr key={index}>
                      <td style={{ fontSize: '0.8rem' }}>{task.task_name}</td>
                      {monthDays.map((day, dayIndex) => (
                        <DailyHoursCell key={day} hours={task.daily_hours?.[dayIndex]} />
                      ))}
                      <td className="text-center fw-bold"><ReportHours value={task.total_hours} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-primary">
                    <td><strong>Totaal per dag</strong></td>
                    {monthDays.map((day, dayIndex) => (
                      <td key={day} className="text-center fw-bold">
                        {employee.daily_totals?.[dayIndex] > 0
                          ? <ReportHours value={employee.daily_totals[dayIndex]} />
                          : '-'}
                      </td>
                    ))}
                    <td className="text-center fw-bold"><ReportHours value={employee.monthly_total} /></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const SingleEmployeeView = ({ data }) => {
    if (!data || !data.tasks) {
      return (
        <div className="text-center text-muted py-5">
          <h5>Geen data beschikbaar</h5>
        </div>
      );
    }

    const monthDays =
      data.month_days ||
      Array.from({ length: data.days_in_month || 31 }, (_, i) => i + 1);

    return (
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{data.period}</h5>
            <div className="text-end">
              <strong>Totaal: <ReportHours value={data.monthly_total} /> uur</strong>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-12">
              <h4>{data.employee_name}</h4>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Taak</th>
                  {monthDays.map((day) => (
                    <th
                      key={day}
                      className="text-center"
                      style={{ minWidth: '40px', fontSize: '0.8rem' }}
                    >
                      {day}
                    </th>
                  ))}
                  <th className="text-center fw-bold" style={{ minWidth: '60px' }}>
                    Totaal
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.tasks.map((task, index) => (
                  <tr key={index}>
                    <td style={{ minWidth: '200px' }}>{task.task_name}</td>
                    {monthDays.map((day, dayIndex) => (
                      <DailyHoursCell key={day} hours={task.daily_hours?.[dayIndex]} />
                    ))}
                    <td className="text-center fw-bold"><ReportHours value={task.total_hours} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-primary">
                <tr>
                  <td><strong>Totaal per dag</strong></td>
                  {monthDays.map((day, dayIndex) => (
                    <td key={day} className="text-center fw-bold">
                      {data.daily_totals?.[dayIndex] > 0
                        ? <ReportHours value={data.daily_totals[dayIndex]} />
                        : '-'}
                    </td>
                  ))}
                  <td className="text-center fw-bold"><ReportHours value={data.monthly_total} /></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const MultipleEmployeesView = ({ data }) => {
    if (!data || !data.employees) {
      return (
        <div className="text-center text-muted py-5">
          <h5>Geen data beschikbaar</h5>
        </div>
      );
    }

    const monthDays =
      data.month_days ||
      Array.from({ length: data.days_in_month || 31 }, (_, i) => i + 1);

    return (
      <div>
        <div className="card mb-4">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{data.period}</h5>
              <div className="text-muted">{data.employees.length} medewerker(s)</div>
            </div>
          </div>
        </div>

        <div className="d-block d-lg-none">
          {data.employees.map((employee, index) => (
            <EmployeeCard key={index} employee={employee} monthDays={monthDays} />
          ))}
        </div>

        <div className="d-none d-lg-block">
          {data.employees.map((employee, index) => (
            <div key={index} className="card mb-4">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">{employee.employee_name}</h6>
                  <div className="text-end">
                    <strong>Totaal: <ReportHours value={employee.monthly_total} /> uur</strong>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Taak</th>
                        {monthDays.map((day) => (
                          <th
                            key={day}
                            className="text-center"
                            style={{ minWidth: '40px', fontSize: '0.8rem' }}
                          >
                            {day}
                          </th>
                        ))}
                        <th className="text-center fw-bold" style={{ minWidth: '60px' }}>
                          Totaal
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(employee.tasks || []).map((task, taskIndex) => (
                        <tr key={taskIndex}>
                          <td style={{ minWidth: '200px' }}>{task.task_name}</td>
                          {monthDays.map((day, dayIndex) => (
                            <DailyHoursCell key={day} hours={task.daily_hours?.[dayIndex]} />
                          ))}
                          <td className="text-center fw-bold"><ReportHours value={task.total_hours} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-primary">
                      <tr>
                        <td><strong>Totaal per dag</strong></td>
                        {monthDays.map((day, dayIndex) => (
                          <td key={day} className="text-center fw-bold">
                            {employee.daily_totals?.[dayIndex] > 0
                              ? <ReportHours value={employee.daily_totals[dayIndex]} />
                              : '-'}
                          </td>
                        ))}
                        <td className="text-center fw-bold"><ReportHours value={employee.monthly_total} /></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!reportData) return null;

    if (selectedUser === "all") {
      return <MultipleEmployeesView data={reportData} />;
    }

    return <SingleEmployeeView data={reportData} />;
  };

  return (
    <div className="container-fluid mt-3 hour-reports-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Uren Rapportages</h2>
        <button
          className="btn btn-outline-primary"
          onClick={() => {}}
        >
          📊 Exporteer
        </button>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <label className="form-label">Maand</label>
          <select
            className="form-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Selecteer maand</option>
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <label className="form-label">Medewerker</label>
          <select
            className="form-select"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="all">👥 Alle Medewerkers</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12 col-lg-4 mb-3">
          <label className="form-label">&nbsp;</label>
          <div className="text-muted small">
            {selectedMonth ? (
              <span>
                {months.find((m) => m.value === selectedMonth)?.label}
                <br />
                {selectedUser === "all" ? "Alle medewerkers" : "1 medewerker"}
              </span>
            ) : (
              <span>Selecteer een maand</span>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <div className="mt-2">Rapportage laden...</div>
        </div>
      ) : reportData ? (
        renderContent()
      ) : selectedMonth ? (
        <div className="text-center text-muted py-5">
          <h5>Geen data gevonden</h5>
          <p>Er zijn geen goedgekeurde uren gevonden voor de geselecteerde periode.</p>
        </div>
      ) : null}
    </div>
  );
};

export default HourReports;
